import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, MessageSquare, Bot, Check, ArrowRight } from "lucide-react";
import { api, resolveImage } from "@/lib/api";
import { PHONE, TEL, SMS } from "@/lib/config";
import { PrimaryBtn, OutlineBtn, FinancingNote } from "@/components/site/shared";
import { QuoteForm } from "@/components/site/QuoteForm";
import { openGhost } from "@/components/site/GhostAI";
import { useContent } from "@/lib/ContentContext";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { content } = useContent();
  const [s, setS] = useState(null);
  const [others, setOthers] = useState([]);
  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setS(r.data)).catch(() => setS(null));
    api.get("/services").then((r) => setOthers(r.data.filter((x) => x.slug !== slug))).catch(() => {});
  }, [slug]);

  if (!s) return <div className="min-h-[50vh] flex items-center justify-center text-[var(--text-muted)]">Loading…</div>;

  return (
    <div>
      <section className="relative pt-16 pb-20 bg-[var(--navy)] text-white overflow-hidden">
        <img src={resolveImage(s.image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-7xl mx-auto px-4">
          <Link to="/services" className="text-[var(--aqua)] text-sm hover:underline">← All Services</Link>
          <h1 className="font-head font-extrabold uppercase text-4xl sm:text-5xl tracking-tight mt-3 max-w-3xl">{s.name}</h1>
          <p className="text-white/80 mt-3 max-w-2xl">{s.short_description}</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <PrimaryBtn to={`/get-a-quote?service=${encodeURIComponent(s.name)}`}>Request a Quote</PrimaryBtn>
            <a href={TEL} className="btn-lift bg-white text-[var(--navy)] font-semibold px-6 py-3 rounded-full flex items-center gap-2"><Phone className="w-4 h-4 text-[var(--red)]" /> {PHONE}</a>
            <a href={SMS} className="btn-lift bg-white/10 text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Text Us</a>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-12">
          <div>
            <h2 className="font-head font-bold text-2xl text-[var(--navy)] uppercase">About This Service</h2>
            <p className="text-[var(--text-muted)] mt-3 leading-relaxed">{s.long_description || s.short_description}</p>

            {s.benefits?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-head font-bold text-xl text-[var(--navy)] uppercase">Benefits</h3>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  {s.benefits.map((b) => <div key={b} className="flex items-center gap-2 text-[var(--text-dark)]"><Check className="w-5 h-5 text-[var(--aqua)]" /> {b}</div>)}
                </div>
              </div>
            )}
            {s.surfaces?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-head font-bold text-xl text-[var(--navy)] uppercase">Suitable Surfaces</h3>
                <div className="flex flex-wrap gap-2 mt-4">
                  {s.surfaces.map((x) => <span key={x} className="bg-[var(--surface)] px-3 py-1.5 rounded-full text-sm text-[var(--navy)]">{x}</span>)}
                </div>
              </div>
            )}
            {s.process?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-head font-bold text-xl text-[var(--navy)] uppercase">Our Process</h3>
                <ol className="mt-4 space-y-3">
                  {s.process.map((p, i) => (
                    <li key={i} className="flex gap-3"><span className="shrink-0 h-7 w-7 rounded-full bg-[var(--action)] text-white text-sm font-bold flex items-center justify-center">{i + 1}</span><span className="text-[var(--text-muted)] pt-0.5">{p}</span></li>
                  ))}
                </ol>
              </div>
            )}

            <div className="mt-8 bg-[var(--bg-light)] border border-gray-100 rounded-2xl p-6">
              <p className="text-sm text-[var(--text-muted)] flex items-center gap-2"><Bot className="w-5 h-5 text-[var(--navy)]" /> Have questions about {s.name.toLowerCase()}?
                <button onClick={openGhost} className="text-[var(--action)] font-semibold underline ml-1">Ask Ghost AI</button></p>
            </div>
            <p className="text-sm text-[var(--text-muted)] mt-6">{content.service_area_note}</p>
          </div>

          <div className="lg:sticky lg:top-24 h-fit bg-white border border-gray-100 rounded-2xl card-shadow p-6" data-testid="service-quote-form">
            <h3 className="font-head font-bold text-xl text-[var(--navy)] uppercase mb-4">Request a Free Quote</h3>
            <QuoteForm variant={s.slug === "commercial-soft-wash" ? "commercial" : "general"} source="service_page" />
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="font-head font-bold text-2xl text-[var(--navy)] uppercase mb-6">Related Services</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {others.slice(0, 4).map((o) => (
                <Link key={o.id} to={`/services/${o.slug}`} className="group bg-white border border-gray-100 rounded-xl overflow-hidden btn-lift">
                  <img src={resolveImage(o.image)} alt={o.name} className="h-28 w-full object-cover" />
                  <p className="p-3 text-sm font-semibold text-[var(--navy)]">{o.name}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
